import { useState, useContext, useCallback, useEffect } from 'react';
import { Box, Grid, Divider, Typography, Link } from '@mui/material';
import { Loop } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Hidden from '../../../components/Hidden';
import {
    AppContext,
    SttDialog,
    SttFormCheck,
    SttFormText,
    SttSelectPForm,
} from '../../../sporttia/all';
import {
    generateFormField,
    fullName,
    formatPriceByLocale,
    composeFormData,
    getErrorMessage,
} from '../../../lib/utils';
import { getPath } from '../../Pages';
import { useInteractionsFiles } from '../../../lib/hooks';
import constants from '../../../config/constants';
import useActivitiesService from '../../../services/ActivitiesService';
import SttFullPageSpinner from '../../../sporttia/spinners/SttFullPageSpinner';
import { SttSelectOrAddPupil } from '../../../sporttia/selectors/SttSelectOrAddPupil';

// Hay labels asociados a campos de formulario que son demasiado largos para que sean outlined
// 200204 -> Campo indicando enfermedades, alergias, antecedentes medicos, etc...
const formIDs = [200204];

export default function ModalSignUp({
    rate,
    form,
    privileges,
    onClose,
    paymentForms,
    idSC,
    idMship,
}: any) {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { openFile } = useInteractionsFiles();
    const navigate = useNavigate();

    const [user, setUser] = useState<{
        id: number | undefined;
        name: string;
        fullName?: string;
        surname1?: string;
        surname2?: string;
    }>({
        id: cxt.user?.role === 'SPORTCENTER' ? undefined : cxt.user?.id,
        name: cxt.user?.role === 'SPORTCENTER' ? '' : fullName(cxt.user),
    });

    const [enrollmentPForm, setEnrollmentPForm] = useState<string>(
        (() => {
            if (rate.paymentForms?.[0]) {
                return rate.paymentForms[0];
            }
            if (paymentForms?.[0]) {
                return paymentForms[0];
            }
            return '';
        })(),
    );
    const [feesToReceipts, setFeesToReceipts] = useState(false);
    const [formValues, setFormValues] = useState(form);

    function clearForm() {
        setFormValues(
            formValues?.map(
                ({ answer: _answer, ...restQuestion }: any) => restQuestion,
            ),
        );
    }

    const queryClient = useQueryClient();

    const activitiesService = useActivitiesService();
    const getActivityRateInscriptionsQuery =
        activitiesService.useGetActivityRateInscriptions(rate.id, {}, {
            select: useCallback((data: any) => data.rows, []),
        } as any);

    // Handle query error with useEffect
    useEffect(() => {
        if (getActivityRateInscriptionsQuery.error) {
            cxt.showMessage(
                'E',
                getErrorMessage(getActivityRateInscriptionsQuery.error),
            );
            onClose();
        }
    }, [getActivityRateInscriptionsQuery.error, cxt, onClose]);
    const createActivityRateStudentMutation =
        activitiesService.useCreateActivityRateStudent({
            onSuccess: (response: any) => {
                if (response.tpv) {
                    // Redirect to the tpv container
                    navigate(
                        getPath('tpvSwitcher', {
                            state: { id: response.tpv.id },
                        }),
                    );
                } else if (response.student) {
                    cxt.showMessage('S', t('page.activity.inscriptionOk'));

                    queryClient.setQueryData(
                        getActivityRateInscriptionsQuery.queryKey,
                        (old: any) => ({
                            ...old,
                            rows: [...old.rows, response.student],
                        }),
                    );

                    if (!user.id) {
                        setUser({
                            ...user,
                            name: '',
                        });
                    }
                }

                clearForm();
            },
            onError: (error: any) => {
                cxt.showMessage('E', getErrorMessage(error));
            },
        });

    function getTicket(inscription: any) {
        openFile(`/students/${inscription.id}.pdf`, 'application/pdf');
    }

    function handleFormFieldChange({ name, value }: any) {
        setFormValues(
            formValues.map((question: any) =>
                question.id === parseInt(name)
                    ? { ...question, answer: { value } }
                    : question,
            ),
        );
    }

    const inscribeOtherLink = (
        <Link
            onClick={() => setUser({ id: undefined, name: '' })}
            sx={{
                color: 'var(--brand-text)',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                    textDecoration: 'underline',
                },
            }}
        >
            <Typography variant="body2">
                <Loop style={{ fontSize: 16 }} />
                <span>&nbsp;</span>
                <span>{t('page.activity.signUpAnotherPerson')}</span>
            </Typography>
        </Link>
    );

    // 'inscribirme a mi'
    const inscribeMeLink = (
        <Link
            onClick={() =>
                setUser({ id: cxt.user?.id, name: fullName(cxt.user) })
            }
            sx={{
                color: 'var(--brand-text)',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                    textDecoration: 'underline',
                },
            }}
        >
            <Typography variant="body2">
                <Loop style={{ fontSize: 16 }} />
                <span>&nbsp;</span>
                <span>{t('activity.signMeUp')}</span>
            </Typography>
        </Link>
    );

    if (
        getActivityRateInscriptionsQuery.isLoading ||
        createActivityRateStudentMutation.isPending
    ) {
        return <SttFullPageSpinner />;
    }

    if (!getActivityRateInscriptionsQuery.isSuccess || !rate) {
        return null;
    }

    return (
        <SttDialog
            open
            onClose={onClose}
            title={t('event.inscription')}
            maxWidth={form ? 'md' : 'sm'}
            content={
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: form ? 6 : 12 }}>
                        <Typography variant="body2">
                            {t('page.activity.youAreGoingToSignUp')}
                        </Typography>

                        <Box
                            mt={3}
                            display="flex"
                            justifyContent="space-between"
                        >
                            <div>
                                <Typography variant="caption">
                                    {t('activity.rate')}
                                </Typography>
                                <Typography variant="body1">
                                    {rate.name}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant="caption">
                                    {t('event.inscription')}
                                </Typography>
                                {rate.enrollment ? (
                                    <Box>
                                        <Typography variant="body1">
                                            {formatPriceByLocale(
                                                rate.enrollment,
                                                rate.enrollmentShape?.currency,
                                                rate.enrollmentShape?.locale,
                                            )}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography variant="body1">
                                            {t('payment.free')}
                                        </Typography>
                                    </Box>
                                )}
                            </div>
                            <div>
                                <Typography variant="caption">
                                    {t('labels.fee')}
                                </Typography>
                                <Typography variant="body1">
                                    {rate.fee ? (
                                        <span>{`${formatPriceByLocale(
                                            rate.fee,
                                            rate.feeShape?.currency,
                                            rate.feeShape?.locale,
                                        )} / ${rate.periodLang}`}</span>
                                    ) : (
                                        <span>{t('activity.noFees')}</span>
                                    )}
                                </Typography>
                            </div>
                        </Box>

                        {(
                            getActivityRateInscriptionsQuery.data as unknown as any[]
                        )?.length > 0 && (
                            <>
                                <Box my={3}>
                                    <Divider />
                                </Box>
                                <Box mb={3}>
                                    <Box
                                        mb={1}
                                        display="flex"
                                        justifyContent="space-between"
                                    >
                                        <Typography variant="body2">
                                            <b>
                                                {t(
                                                    'page.activity.signedUpsByYou',
                                                )}
                                                :
                                            </b>
                                        </Typography>
                                    </Box>
                                    <Box>
                                        {(
                                            getActivityRateInscriptionsQuery.data as unknown as any[]
                                        )?.map((inscription: any) => (
                                            <Box
                                                key={inscription.id}
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <span>
                                                    {inscription.user
                                                        ?.fullName ||
                                                        inscription.name}
                                                </span>
                                                <span>&nbsp;</span>
                                                <span>
                                                    (
                                                    <Link
                                                        onClick={() =>
                                                            getTicket(
                                                                inscription,
                                                            )
                                                        }
                                                    >
                                                        {t('labels.ticket')}
                                                    </Link>
                                                    )
                                                </span>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </>
                        )}

                        <Box mb={3}>
                            <>
                                <Box my={3}>
                                    <Divider />
                                </Box>
                                <Box
                                    mb={1}
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Typography variant="body2">
                                        <b>
                                            {t('page.activity.personToSignUp')}:
                                        </b>
                                    </Typography>
                                    {cxt.user?.role !==
                                        constants.roles.sportcenter && (
                                        <Hidden xsDown>
                                            {user.id
                                                ? inscribeOtherLink
                                                : inscribeMeLink}
                                        </Hidden>
                                    )}
                                </Box>
                                {!user.id ||
                                cxt.user?.role ===
                                    constants.roles.sportcenter ? (
                                    <SttSelectOrAddPupil
                                        caption=""
                                        idSC={idSC}
                                        name="user"
                                        defVal={{
                                            id: user.id ?? null,
                                            name: user.name,
                                            surname1: user.surname1,
                                            surname2: user.surname2,
                                        }}
                                        onChange={(pupil) =>
                                            setUser({
                                                id: pupil.id ?? undefined,
                                                name: pupil.name,
                                                surname1: pupil.surname1,
                                                surname2: pupil.surname2,
                                            })
                                        }
                                        onEnterKey={() => {}}
                                        idMship={idMship}
                                    />
                                ) : (
                                    <SttFormText
                                        disabled
                                        name="person"
                                        defVal={user.fullName ?? user.name}
                                    />
                                )}
                                {cxt.user?.role !==
                                    constants.roles.sportcenter && (
                                    <Hidden smUp>
                                        <Box mt={1}>
                                            {user.id
                                                ? inscribeOtherLink
                                                : inscribeMeLink}
                                        </Box>
                                    </Hidden>
                                )}
                            </>
                        </Box>

                        {rate.enrollment > 0 && (
                            <Box mb={3}>
                                <Box
                                    mb={1}
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <Typography variant="body2">
                                        <b>{t('payment.paymentForm')}</b>
                                        <span>
                                            &nbsp;(
                                            {`${t(
                                                'page.activity.signUpPayment',
                                            )},
									${formatPriceByLocale(
                                        rate.enrollment,
                                        rate.enrollmentShape?.currency,
                                        rate.enrollmentShape?.locale,
                                    )}`}
                                            )
                                        </span>
                                    </Typography>
                                </Box>
                                <SttSelectPForm
                                    caption=""
                                    defVal={enrollmentPForm}
                                    activePFs={
                                        rate.paymentForms || paymentForms
                                    }
                                    onChange={({ value }: any) =>
                                        setEnrollmentPForm(value)
                                    }
                                />
                            </Box>
                        )}

                        {rate.fee > 0 && privileges?.includes(25) && (
                            <Box mb={3}>
                                <SttFormCheck
                                    caption={t('page.activity.toReceipt')}
                                    name="fees"
                                    checked={feesToReceipts}
                                    onChange={({ value }: any) =>
                                        setFeesToReceipts(value)
                                    }
                                />
                            </Box>
                        )}
                    </Grid>

                    {form && form.length > 0 && (
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box
                                mb={1}
                                display="flex"
                                justifyContent="space-between"
                            >
                                <Typography variant="body2">
                                    <b>{t('page.homeA.ev.2')}:</b>
                                </Typography>
                            </Box>

                            {formValues.map((item: any) => (
                                <Box mb={3} key={item.it}>
                                    <>
                                        {generateFormField(
                                            item,
                                            handleFormFieldChange,
                                            !!formIDs.includes(item.id),
                                        )}
                                        {!!item.mandatory && (
                                            <span>{`(${t('labels.mandatory')})`}</span>
                                        )}
                                    </>
                                </Box>
                            ))}
                        </Grid>
                    )}
                </Grid>
            }
            buttons={[
                {
                    show: cxt.user?.role !== 'SPORTCENTER',
                    type: 'accept',
                    onClick: () => {
                        createActivityRateStudentMutation.mutate({
                            idRate: rate.id,
                            params: {
                                id: rate.id,
                                idUser: user.id!,
                                name: user.name,
                                surname1: user.surname1,
                                surname2: user.surname2,
                                enrollmentPaymentForm: enrollmentPForm || null,
                                feePaymentForm: feesToReceipts
                                    ? constants.payment.paymentForms.receipt
                                          .name
                                    : '',
                                form:
                                    form && form.length > 0
                                        ? composeFormData(formValues)
                                        : null,
                            } as any,
                        });
                    },
                },
            ]}
        />
    );
}
